/**
 * 3-Layer Permission Service
 *
 * Resolves permissions through three priority layers:
 *   Layer 1 (Feature Flags)  — highest priority, gates entire feature namespaces
 *   Layer 2 (Subscription)   — module-level entitlements from the user's plan
 *   Layer 3 (Role Permissions)— granular resource.action grants from the user's role
 *
 * A permission is granted only when ALL three layers allow it.
 * If a layer is not loaded (null), it acts as pass-through (allow).
 *
 * Permission key formats:
 *   {module}.{action}                 — e.g., "camera.view", "device.edit"
 *   {module}.{subfeature}.{action}    — e.g., "camera.ai-search.view"
 *
 * Feature flag keys map to permission prefixes:
 *   flag "camera.ai-search" = false  →  blocks "camera.ai-search.*"
 *   flag "camera" = false            →  blocks "camera.*"
 *
 * Subscription entitlements are a key-value map:
 *   { "camera": true, "thermostat": true }           →  allows "camera.*" and "thermostat.*"
 *   { "camera.ai-search": true, "thermostat": true }  →  allows "camera.ai-search.*" and "thermostat.*"
 *   { "camera.limit": 30, "camera.resolution": 1080 } →  entitled + queryable limits
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAMPLE API MESSAGE FORMATS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ▸ Layer 1 — Feature Flags
 *   Source of truth: src/config/envFlagMap.ts + .env.{environment} files
 *   Runtime override: loadFeatureFlags(flags) — merges on top of env defaults
 *   Direct query: isFeatureEnabled(flagKey) — Layer 1 only, no Layer 2/3 check
 *
 *   Feature flags serve TWO purposes:
 *     1. Permission gating — flags with permission-style keys (e.g., "camera.ai-search")
 *        block all matching permissions via prefix matching in hasPermission().
 *     2. Navigation gating — flags with nav-item keys (e.g., "billing", "hvac.status")
 *        control menu visibility in Layout.tsx via isFeatureEnabled().
 *
 *   Default values are defined in .env.{environment} via VITE_FF_* variables.
 *   The full list of supported keys is in src/config/envFlagMap.ts.
 *   See also: src/services/permissionService.readme for the complete flag reference.
 *
 *   • Flags are loaded from env vars at construction time
 *   • loadFeatureFlags() reloads env flags first, then applies overrides
 *   • isFeatureEnabled(key) returns the flag value (true if key not set)
 *   • Flag key not in map → enabled by default
 *   • Flag key set to false → disabled
 *
 *   Runtime override via API (future, applied on top of JSON defaults):
 *   Array of disabled feature names:
 *   [
 *     "camera.ai-search",
 *     "thermostat.scheduling",
 *     "panel"
 *   ]
 *
 *   Also accepts Record<string, boolean> for backward compatibility:
 *   {
 *     "camera.ai-search": false,
 *     "thermostat.scheduling": false,
 *     "panel": false
 *   }
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ▸ Layer 2 — Subscription Entitlements API Response
 *   Loaded via: loadSubscriptionEntitlements(entitlements)
 *
 *   {
 *     "camera": true,
 *     "camera.limit": 30,
 *     "camera.resolution": "1080p",
 *     "camera.ai-search": true,
 *     "camera.clips": true,
 *     "thermostat": true,
 *     "thermostat.scheduling": true,
 *     "billing": true,
 *     "panel": true,
 *     "panel.limit": 5
 *   }
 *
 *   • Key = module or module.feature (dot notation)
 *   • Value = true (entitled), number (entitled + limit), or string (entitled + limit)
 *   • Falsy values (false, 0, "") = not entitled
 *   • Module-level key (e.g., "camera": true) grants access to ALL camera.* permissions
 *   • Feature-level key (e.g., "camera.ai-search": true) grants only camera.ai-search.*
 *   • Limit keys (e.g., "camera.limit": 30) are entitled AND queryable via getSubscriptionLimit()
 *   • Pass null to deactivate the layer entirely (pass-through)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ▸ Layer 3 — Role Permissions API Response
 *   Loaded via: loadPermissions(permissions)
 *
 *   The API returns the user's role object. Only the permissions[] array is needed:
 *
 *   API Response:
 *   {
 *     "roleName": "ADMIN",
 *     "permissions": [
 *       { "resource": "DEVICE", "actions": ["READ", "UPDATE", "CREATE", "DELETE"] },
 *       { "resource": "USER", "actions": ["READ", "UPDATE", "INVITE", "ASSIGN_ROLE"] }
 *     ]
 *   }
 *
 *   Usage — pass only the permissions array:
 *     loadPermissions(apiResponse.permissions)
 *
 *   Format A — Dot-notation strings (pre-converted or new API):
 *   [
 *     "camera.view", "camera.edit", "camera.delete",
 *     "camera.ai-search.view", "camera.ai-search.configure",
 *     "thermostat.view", "thermostat.configure",
 *     "user.manage", "user.invite",
 *     "billing.view", "billing.manage"
 *   ]
 *
 *   Format B — Legacy (resource + actions), auto-converted internally:
 *   [
 *     { "resource": "DEVICE", "actions": ["READ", "UPDATE", "CREATE", "DELETE"] },
 *     { "resource": "USER", "actions": ["READ", "UPDATE", "INVITE", "ASSIGN_ROLE"] }
 *   ]
 *
 *   • roleName is NOT passed to the service — only the permissions array matters
 *   • 2-segment: "module.action" (e.g., "camera.view")
 *   • 3-segment: "module.subfeature.action" (e.g., "camera.ai-search.view")
 *   • Legacy format is auto-converted to dot notation internally
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type {
  ApiRole,
} from "../types";

/**
 * @deprecated Role parameter is no longer needed. Permission methods no longer require a role argument.
 * Kept as re-export so existing imports don't break.
 */
export type { ApiRole };

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Module IDs used in OnePortal (for dot notation permissions)
 */
export type ModuleId =
  | "camera"
  | "thermostat"
  | "panel"
  | "water-leak"
  | "device"
  | "dashboard"
  | "user"
  | "account"
  | "reports"
  | "services"
  | "subscription"
  | "location"
  | "geofence"
  | "profile"
  | "activity"
  | "branding";

/**
 * Permission actions
 */
export type PermissionAction =
  | "view" // View/read access
  | "edit" // Update/modify
  | "create" // Create new
  | "delete" // Remove/delete
  | "operate" // Control/operate devices
  | "configure" // Configure settings
  | "manage" // Full management access
  | "invite" // Invite users
  | "hide" // Hide elements
  | "status" // Status check
  | "exit" // Exit/leave
  | "map" // Map access
  | "notification_delete" // Delete notifications
  | "notification_update" // Update notifications
  | "notification_view" // View notifications
  | "partner_link" // Link partner
  | "partner_unlink" // Unlink partner
  | "structure_create" // Create structure
  | "structure_delete" // Delete structure
  | "structure_update"; // Update structure

/**
 * Also export as ModuleAction for backward compatibility
 */
export type ModuleAction = PermissionAction;

/**
 * Type-safe permission key constants derived from the rolePermissions API.
 *
 * Format: `{resource}.{action}` — resource and action are lowercase mapped values.
 * Use these instead of raw strings so a future naming change only requires
 * updating this single enum.
 *
 * @example
 * import { PermissionKey } from '@/services/permissionService';
 * hasPermission(PermissionKey.DEVICE_CREATE)   // "device.create"
 * hasPermission(PermissionKey.USER_INVITE)     // "user.invite"
 */
export enum PermissionKey {
  // Account
  ACCOUNT_VIEW = "account.view",
  ACCOUNT_CREATE = "account.create",
  ACCOUNT_EDIT = "account.edit",
  ACCOUNT_DELETE = "account.delete",

  // Activity Feed
  ACTIVITY_FEED_VIEW = "activity_feed.view",

  // Branding
  BRANDING_VIEW = "branding.view",
  BRANDING_HIDE = "branding.hide",

  // Device
  DEVICE_VIEW = "device.view",
  DEVICE_CREATE = "device.create",
  DEVICE_EDIT = "device.edit",
  DEVICE_DELETE = "device.delete",
  DEVICE_OPERATE = "device.operate",
  DEVICE_CONFIGURE = "device.configure",
  DEVICE_UPDATE = "device.update",
  DEVICE_MANAGE = "device.manage",

  // Geofence
  GEOFENCE_VIEW = "geofence.view",
  GEOFENCE_CREATE = "geofence.create",
  GEOFENCE_EDIT = "geofence.edit",
  GEOFENCE_DELETE = "geofence.delete",
  GEOFENCE_STATUS = "geofence.status",

  // Location
  LOCATION_VIEW = "location.view",
  LOCATION_CREATE = "location.create",
  LOCATION_EDIT = "location.edit",
  LOCATION_DELETE = "location.delete",
  LOCATION_EXIT = "location.exit",
  LOCATION_INVITE = "location.invite",
  LOCATION_MAP = "location.map",
  LOCATION_NOTIFICATION_DELETE = "location.notification_delete",
  LOCATION_NOTIFICATION_UPDATE = "location.notification_update",
  LOCATION_NOTIFICATION_VIEW = "location.notification_view",
  LOCATION_PARTNER_LINK = "location.partner_link",
  LOCATION_PARTNER_UNLINK = "location.partner_unlink",
  LOCATION_STRUCTURE_CREATE = "location.structure_create",
  LOCATION_STRUCTURE_DELETE = "location.structure_delete",
  LOCATION_STRUCTURE_UPDATE = "location.structure_update",

  // Profile
  PROFILE_VIEW = "profile.view",
  PROFILE_CREATE = "profile.create",
  PROFILE_EDIT = "profile.edit",

  // Reports
  REPORTS_VIEW = "reports.view",
  REPORTS_MANAGE = "reports.manage",

  // Services
  SERVICES_VIEW = "services.view",
  SERVICES_EDIT = "services.edit",
  SERVICES_MANAGE = "services.manage",

  // Subscription
  SUBSCRIPTION_VIEW = "subscription.view",
  SUBSCRIPTION_CREATE = "subscription.create",
  SUBSCRIPTION_EDIT = "subscription.edit",
  SUBSCRIPTION_DELETE = "subscription.delete",

  // User
  USER_VIEW = "user.view",
  USER_CREATE = "user.create",
  USER_DELETE = "user.delete",
  USER_INVITE = "user.invite",
  USER_MANAGE = "user.manage",
}

/**
 * Feature flags input: either an array of disabled feature names (primary)
 * or a Record of namespace → boolean (backward compatibility).
 *
 * By default ALL features are enabled. The API returns only disabled features.
 *
 * Key format: "module" or "module.subfeature"
 *
 * @example
 * // Primary: array of disabled features (from API)
 * ["camera.ai-search", "thermostat", "panel"]
 *
 * // Backward compat: Record format
 * { "camera.ai-search": false, "thermostat": false }
 */
export type FeatureFlagMapping = Record<string, boolean>;

/**
 * Feature flags input type: array of disabled feature names or key-value map.
 */
export type FeatureFlagInput = string[] | FeatureFlagMapping;

/**
 * Subscription entitlement value: `true` for simple allow, or a number/string limit.
 * Any truthy value means the module/feature is entitled.
 *
 * @example
 * true    — module/feature is allowed
 * 30      — allowed, with a numeric limit (e.g., max 30 cameras)
 * "1080p" — allowed, with a string limit (e.g., max resolution)
 */
export type SubscriptionEntitlementValue = boolean | number | string;

/**
 * Subscription entitlements: key-value map of module/feature → value.
 *
 * Keys follow the same dot notation as permissions:
 *   - Module-level: "camera" → grants access to all camera.* permissions
 *   - Feature-level: "camera.ai-search" → grants only camera.ai-search.*
 *   - Limit keys: "camera.limit", "camera.resolution" → entitled + queryable value
 *
 * Any truthy value (true, non-zero number, non-empty string) means entitled.
 *
 * @example
 * { "camera": true, "thermostat": true }            // module-level
 * { "camera.ai-search": true, "camera.clips": true } // feature-level
 * { "camera": true, "camera.limit": 30, "camera.resolution": "1080p" } // with limits
 */
export type SubscriptionEntitlements = Record<string, SubscriptionEntitlementValue>;

// ============================================================================
// Permission Service
// ============================================================================

import { ENV_FLAG_MAP } from "../config/envFlagMap";

export class PermissionService {
  // Layer 3: User's granted permissions (lowest priority)
  private permissions: Set<string> = new Set();

  // Layer 1: Feature flags mapped to permission namespaces (highest priority)
  private featureFlags: Map<string, boolean> = new Map();

  // Layer 2: Subscription entitlements — module or module.feature keys with values (middle priority)
  // null = layer not loaded / not applicable (pass-through)
  private subscriptionEntitlements: Map<string, SubscriptionEntitlementValue> | null = null;

  // Cache with generation counter for invalidation across layer reloads
  private permissionCache: Map<string, boolean> = new Map();
  private cacheGeneration: number = 0;

  constructor() {
    // Load feature flags from VITE_FF_* environment variables
    this.applyEnvFlagOverrides(this.featureFlags);
  }

  /**
   * Applies VITE_FF_* environment variable overrides onto the given flags map.
   * Only overrides entries that are explicitly set in the environment.
   */
  private applyEnvFlagOverrides(flags: Map<string, boolean>): void {
    for (const [envKey, flagKey] of Object.entries(ENV_FLAG_MAP)) {
      const envValue = import.meta.env[envKey];
      if (envValue !== undefined) {
        flags.set(flagKey, envValue === 'true' || envValue === true);
      }
    }
  }

  // ============================================================================
  // Layer 1: Feature Flags
  // ============================================================================

  /**
   * Load feature flags with optional runtime overrides.
   *
   * Always reloads flags from VITE_FF_* env vars first, then applies
   * caller-supplied overrides on top. This ensures env defaults are
   * the baseline and runtime API data can selectively override them.
   *
   * When called with no arguments, resets flags to env defaults.
   * This is useful after clear() to restore the default flag state.
   *
   * @param flags - Optional overrides: array of disabled feature names,
   *                or Record of namespace → boolean. Omit to reload defaults only.
   *
   * @example
   * // Reload defaults only (no overrides)
   * loadFeatureFlags();
   *
   * // Apply API overrides on top of defaults
   * loadFeatureFlags(["camera.ai-search", "thermostat"]);
   *
   * // Record format (backward compatibility)
   * loadFeatureFlags({
   *   "camera.ai-search": false,
   *   "thermostat": false,
   * });
   */
  loadFeatureFlags(flags?: FeatureFlagInput): void {
    this.featureFlags.clear();

    // Load feature flags from VITE_FF_* environment variables
    this.applyEnvFlagOverrides(this.featureFlags);

    // Then apply caller-supplied overrides on top
    if (flags) {
      if (Array.isArray(flags)) {
        // Array of disabled feature names — all entries are disabled
        for (const key of flags) {
          this.featureFlags.set(key, false);
        }
      } else {
        // Record format (backward compatibility)
        for (const [key, value] of Object.entries(flags)) {
          this.featureFlags.set(key, value);
        }
      }
    }

    this.invalidateCache();
  }

  /**
   * Check Layer 1: Is the permission allowed by feature flags?
   *
   * Iterates all loaded flag keys. If a flag key is a prefix of the
   * requested permission (or matches exactly) and the flag is disabled,
   * the permission is denied.
   *
   * When no matching flag exists, the layer passes through (allowed).
   */
  private isFeatureFlagAllowed(permission: string): boolean {
    if (this.featureFlags.size === 0) return true; // No flags loaded → pass-through

    for (const [flagKey, enabled] of this.featureFlags) {
      // Check if the flag key matches or is a prefix of the permission
      // "camera.ai-search" matches "camera.ai-search.view" and "camera.ai-search"
      // "camera" matches "camera.view", "camera.ai-search.view", etc.
      if (permission === flagKey || permission.startsWith(flagKey + ".")) {
        if (!enabled) return false;
      }
    }

    return true;
  }

  // ============================================================================
  // Layer 2: Subscription Entitlements
  // ============================================================================

  /**
   * Load subscription entitlements (module-level and/or feature-level with limits).
   *
   * Accepts a key-value map where keys are module or module.feature strings
   * and values are `true` (allowed), a number (limit), or a string (limit).
   * Any truthy value means the module/feature is entitled.
   *
   * Pass `null` to deactivate the subscription layer (pass-through).
   *
   * @param entitlements - Record of module/feature keys → values, or null to deactivate
   *
   * @example
   * loadSubscriptionEntitlements({ camera: true, thermostat: true });
   * loadSubscriptionEntitlements({ "camera": true, "camera.limit": 30, "camera.resolution": "1080p" });
   * loadSubscriptionEntitlements({ "camera.ai-search": true, "camera.clips": true });
   * loadSubscriptionEntitlements(null); // deactivate layer
   */
  loadSubscriptionEntitlements(entitlements: SubscriptionEntitlements | null): void {
    if (entitlements === null) {
      this.subscriptionEntitlements = null;
    } else {
      this.subscriptionEntitlements = new Map(Object.entries(entitlements));
    }
    this.invalidateCache();
  }

  /**
   * Check Layer 2: Is the permission allowed by subscription entitlements?
   *
   * Checks if any entitlement key with a truthy value is a prefix of (or
   * equal to) the requested permission using dot-delimited matching.
   *
   * Examples:
   *   { "camera": true }            allows "camera.view", "camera.ai-search.view"
   *   { "camera.ai-search": true }  allows "camera.ai-search.view" but NOT "camera.view"
   *   { "camera.limit": 30 }        allows "camera.limit" (truthy value = entitled)
   *
   * When subscriptionEntitlements is null (not loaded), the layer passes through.
   */
  private isSubscriptionAllowed(permission: string): boolean {
    if (this.subscriptionEntitlements === null) return true; // Layer not active / loading → pass-through

    if (permission.startsWith("clip.")) {
      const aiSearchEntitlement = this.subscriptionEntitlements.get("camera.ai-search");
      if (permission === "clip.semantic_search" && aiSearchEntitlement === false) {
        return false;
      }
      return true;
    }

    // Device-level admin permissions are governed by role only, not by subscription.
    if (permission.startsWith("device.")) return true;

    for (const [key, value] of this.subscriptionEntitlements) {
      // Only consider truthy values as entitled
      if (!value) continue;

      if (permission === key || permission.startsWith(key + ".")) {
        return true;
      }
    }

    return false;
  }

  // ============================================================================
  // Layer 3: Permissions
  // ============================================================================

  /**
   * Load the user's granted permissions.
   *
   * Pass the raw permissions array from the API response.
   * The roleName is not needed — only the permissions matter.
   *
   * Permission arrays can be:
   * - Dot-notation strings: ["camera.view", "camera.ai-search.configure"]
   * - Legacy format: [{ resource: "DEVICE", actions: ["READ"] }]
   *
   * @param permissionsData - Array of permission strings or legacy objects
   *
   * @example
   * // Dot-notation (new)
   * loadPermissions(["camera.view", "camera.edit", "camera.ai-search.view"]);
   *
   * // Legacy format (auto-converted)
   * loadPermissions([
   *   { resource: "DEVICE", actions: ["READ", "UPDATE"] },
   *   { resource: "USER", actions: ["READ", "INVITE"] }
   * ]);
   */
  loadPermissions(permissionsData: any[]): void {
    this.permissions.clear();

    if (!Array.isArray(permissionsData) || permissionsData.length === 0) {
      this.invalidateCache();
      return;
    }

    if (typeof permissionsData[0] === "string") {
      // New format: ["camera.view", "device.edit", "camera.ai-search.view"]
      (permissionsData as string[]).forEach((permission) => {
        this.permissions.add(permission);
      });
    } else {
      // Old format: [{resource: "DEVICE", actions: ["READ"]}]
      (permissionsData as any[]).forEach((perm: any) => {
        if (perm.resource && perm.actions) {
          const resource = perm.resource.toLowerCase();
          perm.actions.forEach((action: string) => {
            const dotAction = this.convertActionToDotNotation(action, resource);
            this.permissions.add(`${resource}.${dotAction}`);

            // Add device-specific permissions for DEVICE resource
            if (resource === "device") {
              this.permissions.add(`camera.${dotAction}`);
              this.permissions.add(`thermostat.${dotAction}`);
              this.permissions.add(`panel.${dotAction}`);
              this.permissions.add(`water-leak.${dotAction}`);
            }
          });
        }
      });
    }

    this.invalidateCache();
  }

  /**
   * Check Layer 3: Is the permission in the loaded set?
   */
  private isPermissionGranted(permission: string): boolean {
    return this.permissions.has(permission);
  }

  // ============================================================================
  // Unified Permission Resolution (3-Layer)
  // ============================================================================

  /**
   * Check if a permission is allowed through all three layers.
   *
   * Resolution order (short-circuit deny):
   *   1. Feature Flag layer — if a matching flag is disabled → denied
   *   2. Subscription layer — if the module is not entitled → denied
   *   3. Permission layer — if the permission is not in the loaded set → denied
   *
   * @param permission - Permission in dot notation
   *   Format: "module.action" or "module.subfeature.action"
   *
   * @example
   * hasPermission('camera.view')             // 2-segment
   * hasPermission('camera.ai-search.view')   // 3-segment
   */
  hasPermission(permission: string): boolean {
    // Check cache (includes generation for invalidation)
    const cacheKey = `${this.cacheGeneration}:${permission}`;
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey)!;
    }

    // Layer 1: Feature Flags (highest priority)
    if (!this.isFeatureFlagAllowed(permission)) {
      this.permissionCache.set(cacheKey, false);
      return false;
    }

    // Layer 2: Subscription Entitlements
    if (!this.isSubscriptionAllowed(permission)) {
      this.permissionCache.set(cacheKey, false);
      return false;
    }

    // Layer 3: Loaded Permissions
    const allowed = this.isPermissionGranted(permission);
    this.permissionCache.set(cacheKey, allowed);
    return allowed;
  }

  /**
   * Check non-role permission layers only.
   * Useful when a caller supplies its own role permission decision but still
   * needs feature flag and subscription entitlement gates to apply.
   */
  isPermissionAvailable(permission: string): boolean {
    return this.isFeatureFlagAllowed(permission) && this.isSubscriptionAllowed(permission);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  // Per-resource overrides applied before the generic actionMap fallback.
  private static readonly RESOURCE_ACTION_OVERRIDES: Record<string, Record<string, string>> = {
    clip: { READ: "read" },
  };

  private static readonly ACTION_MAP: Record<string, string> = {
    READ: "view",
    UPDATE: "edit",
    CREATE: "create",
    DELETE: "delete",
    OPERATE: "operate",
    CONFIGURE: "configure",
    ASSIGN_ROLE: "manage",
    UPDATE_ROLE: "manage",
    INVITE: "invite",
    SUBSCRIBE: "manage",
    UNSUBSCRIBE: "manage",
    HIDE: "hide",
    REMOVE: "delete",
    GRANT_CONSENT: "manage",
  };

  /**
   * Convert API actions to dot notation
   */
  private convertActionToDotNotation(action: string, resource?: string): string {
    const override = resource
      ? PermissionService.RESOURCE_ACTION_OVERRIDES[resource]?.[action]
      : undefined;
    return override ?? PermissionService.ACTION_MAP[action] ?? action.toLowerCase();
  }

  /**
   * Invalidate the permission cache (called when any layer is reloaded)
   */
  private invalidateCache(): void {
    this.cacheGeneration++;
    this.permissionCache.clear();
  }

  // ============================================================================
  // Public Query Methods
  // ============================================================================

  /**
   * Check if permissions are loaded (Layer 3 has data)
   */
  isLoaded(): boolean {
    return this.permissions.size > 0;
  }

  /**
   * Clear all three layers and the cache
   */
  clear(): void {
    this.permissions.clear();
    this.featureFlags.clear();
    this.subscriptionEntitlements = null;
    this.invalidateCache();
  }

  /**
   * Check if user can access a module (has any permission for it).
   * Also verifies against subscription entitlements and feature flags.
   *
   * @example
   * canAccessModule('camera') // true (has camera.view)
   */
  canAccessModule(moduleId: ModuleId): boolean {
    // Layer 2 quick check: if subscription is loaded and no entitlement covers this module, deny
    if (this.subscriptionEntitlements !== null) {
      const hasModuleEntitlement = [...this.subscriptionEntitlements.entries()].some(
        ([key, value]) => !!value && (key === moduleId || key.startsWith(moduleId + "."))
      );
      if (!hasModuleEntitlement) return false;
    }

    // Layer 1 quick check: if the entire module is feature-flagged off, deny
    if (this.featureFlags.has(moduleId) && !this.featureFlags.get(moduleId)) {
      return false;
    }

    // Special case: dashboard is accessible to all authenticated users
    if (moduleId === "dashboard") {
      return (
        this.hasPermission("dashboard.view") ||
        this.hasPermission("profile.view")
      );
    }

    // Check if user has any permission for this module that passes all layers
    for (const permission of this.permissions) {
      if (permission.startsWith(`${moduleId}.`)) {
        // Verify this specific permission passes feature flag and subscription layers
        if (this.isFeatureFlagAllowed(permission) && this.isSubscriptionAllowed(permission)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check module permission with specific action
   *
   * @example
   * hasModulePermission('camera', 'view') // true
   * hasModulePermission('user', 'delete') // false
   */
  hasModulePermission(
    moduleId: ModuleId,
    action: PermissionAction
  ): boolean {
    return this.hasPermission(`${moduleId}.${action}`);
  }

  /**
   * Check if user has ANY of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) =>
      this.hasPermission(permission)
    );
  }

  /**
   * Check if user has ALL of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every((permission) =>
      this.hasPermission(permission)
    );
  }

  /**
   * Get all loaded permissions (raw Layer 3 data, unfiltered).
   */
  getPermissions(): string[] {
    return Array.from(this.permissions);
  }

  /**
   * Get effective permissions after applying all three layers.
   *
   * Returns only the permissions that pass feature flag and subscription
   * checks. Useful for debugging and UI rendering.
   *
   * @returns Array of allowed permission strings
   */
  getEffectivePermissions(): string[] {
    const effective: string[] = [];

    for (const permission of this.permissions) {
      // Check Layer 1: Feature flags
      if (!this.isFeatureFlagAllowed(permission)) continue;

      // Check Layer 2: Subscription entitlements
      if (!this.isSubscriptionAllowed(permission)) continue;

      // Passed all layers
      effective.push(permission);
    }

    return effective;
  }

  /**
   * Get all modules that the user can access (filtered through all layers)
   */
  getAccessibleModules(): ModuleId[] {
    const modules: ModuleId[] = [
      "camera",
      "thermostat",
      "panel",
      "water-leak",
      "device",
      "dashboard",
      "user",
      "account",
      "reports",
      "services",
      "subscription",
      "location",
      "geofence",
      "profile",
    ];

    return modules.filter((module) => this.canAccessModule(module));
  }

  /**
   * Get the limit value for a subscription entitlement key.
   *
   * Returns the raw value (number or string) if the key exists and has a
   * non-boolean value, or `undefined` if not found / boolean.
   *
   * @param key - The entitlement key to look up (e.g., "camera.limit", "camera.resolution")
   * @returns The limit value, or undefined if not set
   *
   * @example
   * getSubscriptionLimit("camera.limit")       // 30
   * getSubscriptionLimit("camera.resolution")   // "1080p"
   * getSubscriptionLimit("camera")              // undefined (boolean entitlement)
   * getSubscriptionLimit("unknown.key")         // undefined
   */
  getSubscriptionLimit(key: string): number | string | undefined {
    if (this.subscriptionEntitlements === null) return undefined;

    const value = this.subscriptionEntitlements.get(key);
    if (value === undefined || typeof value === "boolean") return undefined;

    return value;
  }

  /**
   * Check if a subscription entitlement key exists and has a truthy value.
   *
   * Useful for checking entitlement without prefix-matching semantics.
   * Returns `true` if the exact key exists with a truthy value, or if
   * subscription layer is not loaded (pass-through).
   *
   * @param key - The exact entitlement key to check
   *
   * @example
   * hasSubscriptionEntitlement("camera")          // true if { camera: true }
   * hasSubscriptionEntitlement("camera.limit")    // true if { "camera.limit": 30 }
   */
  hasSubscriptionEntitlement(key: string): boolean {
    if (this.subscriptionEntitlements === null) return true; // pass-through
    const value = this.subscriptionEntitlements.get(key);
    return !!value;
  }

  /**
   * Check if a specific feature flag is enabled.
   *
   * Returns `true` if the flag key exists and is enabled, or if the key
   * is not present (features are enabled by default when not explicitly set).
   * Returns `false` only when the flag is explicitly set to `false`.
   *
   * @param flagKey - The feature flag key (e.g., "hvac.status", "camera.ai-search")
   *
   * @example
   * isFeatureEnabled("hvac.status")    // false (disabled by default)
   * isFeatureEnabled("camera.view")    // true  (not set = enabled)
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (!this.featureFlags.has(flagKey)) return true; // Not set → enabled by default
    return this.featureFlags.get(flagKey)!;
  }

  /**
   * Get statistics about loaded permissions (for debugging)
   */
  getStats() {
    return {
      totalPermissions: this.permissions.size,
      featureFlagsLoaded: this.featureFlags.size,
      subscriptionEntitlementsLoaded: this.subscriptionEntitlements
        ? this.subscriptionEntitlements.size
        : null,
      subscriptionLimits: this.subscriptionEntitlements
        ? Object.fromEntries(
            [...this.subscriptionEntitlements.entries()].filter(
              ([, v]) => typeof v === "number" || typeof v === "string"
            )
          )
        : null,
      cacheSize: this.permissionCache.size,
      cacheGeneration: this.cacheGeneration,
    };
  }

  // ============================================================================
  // Backward Compatibility (for migration period)
  // ============================================================================

  /**
   * @deprecated Use hasPermission() with dot notation instead
   * Legacy format support: "module.{moduleId}.{action}"
   */
  hasResourcePermission(
    resource: string,
    action: string
  ): boolean {
    // Convert to dot notation
    const dotAction = this.convertActionToDotNotation(action, resource);
    const permission = `${resource.toLowerCase()}.${dotAction}`;
    return this.hasPermission(permission);
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
